'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import {
  CheckmarkCircle02Icon,
  Clock01Icon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/styled/text';
import { ChatCodeBlock } from '../../code-block/ChatCodeBlock';

type TerminalRendererProps = {
  data: unknown;
  maxHeight?: string;
  compact?: boolean;
};
type TerminalOutput = {
  command?: string;
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  duration?: number;
};

function isTerminalOutput(data: unknown): data is TerminalOutput {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.command === 'string' ||
    typeof obj.stdout === 'string' ||
    typeof obj.stderr === 'string'
  );
}

function parseTerminalOutputFromString(data: string): TerminalOutput | null {
  const commandMatch = data.match(/Command: (.+)/);
  const exitCodeMatch = data.match(/Exit Code: (\d+)/);
  const durationMatch = data.match(/Duration: (\d+)ms/);
  const stdoutMatch = data.match(
    /=== STDOUT ===\n([\s\S]*?)(?:\n\n=== STDERR ===|$)/,
  );
  const stderrMatch = data.match(/=== STDERR ===\n([\s\S]*?)$/);

  if (!commandMatch) {
    return null;
  }

  return {
    command: commandMatch[1].trim(),
    exitCode: exitCodeMatch ? parseInt(exitCodeMatch[1], 10) : undefined,
    duration: durationMatch ? parseInt(durationMatch[1], 10) : undefined,
    stdout: stdoutMatch ? stdoutMatch[1].trim() : undefined,
    stderr: stderrMatch ? stderrMatch[1].trim() : undefined,
  };
}

export function TerminalRenderer({ data, maxHeight }: TerminalRendererProps) {
  const t = useTranslations('designSystem.chat');
  let terminalOutput: TerminalOutput | null = null;

  if (isTerminalOutput(data)) {
    terminalOutput = data;
  } else if (typeof data === 'string') {
    terminalOutput = parseTerminalOutputFromString(data);
  }

  if (!terminalOutput) {
    return (
      <ChatCodeBlock
        code={String(data)}
        size="sm"
        maxHeight={maxHeight || '300px'}
        wrap
      />
    );
  }

  const { command, stdout, stderr, exitCode, duration } = terminalOutput;
  const isSuccess = exitCode === 0 || exitCode === undefined;

  return (
    <Box width="100%">
      {/* Command + Status Header */}
      <HStack gap={2} mb={2} flexWrap="wrap">
        <Text fontFamily="mono" fontSize="xs" color="fg.muted">
          <Text as="span" color="fg.subtle">
            ${' '}
          </Text>
          {command}
        </Text>

        <HStack gap={2} ml="auto">
          {exitCode !== undefined && (
            <HStack gap={1}>
              {isSuccess ? (
                <CheckmarkCircle02Icon
                  size={12}
                  color="var(--colors-fg-success)"
                />
              ) : (
                <CancelCircleIcon size={12} color="var(--colors-fg-error)" />
              )}
              <Text
                fontSize="2xs"
                color={isSuccess ? 'fg.success' : 'fg.error'}
                fontFamily="mono"
              >
                {exitCode}
              </Text>
            </HStack>
          )}
          {duration !== undefined && (
            <HStack gap={1}>
              <Clock01Icon size={10} color="var(--colors-fg-muted)" />
              <Text fontSize="2xs" color="fg.muted" fontFamily="mono">
                {t('runtimeMs', { ms: duration })}
              </Text>
            </HStack>
          )}
        </HStack>
      </HStack>

      {/* Output */}
      {stdout ? (
        <ChatCodeBlock
          code={stdout}
          size="sm"
          maxHeight={maxHeight || '300px'}
          wrap
        />
      ) : null}
      {stderr ? (
        <ChatCodeBlock
          code={stderr}
          size="sm"
          tone="error"
          maxHeight={maxHeight || '300px'}
          wrap
        />
      ) : null}
    </Box>
  );
}
